import { type UIEvent, useEffect, useState, type FC, Fragment } from 'react'
import { observer } from 'mobx-react-lite'
import { Scrollbar, SpinnerLoader, Typography } from 'shared/ui'
import { InboxDropdownCard } from 'entities/Inbox'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { type SearchDropdownInboxesStore } from 'widgets/SearchDropdown/store/searchDropdownInboxesStore'
import { getLimitReachedTooltip } from 'widgets/SearchDropdown/constants/limitReachedTooltip'
import styles from './styles.module.scss'

type IInboxesDropdownContentItemsProps = {
  store: SearchDropdownInboxesStore
  onChange?: (item: Inbox) => void
}

export const InboxesDropdownContentItems: FC<IInboxesDropdownContentItemsProps> = observer(
  ({ store, onChange }) => {
    const {
      options,
      itemsListIds,
      loading,
      hasMore,
      loadMore,
      hasItems,
      init,
      getItemById,
      maxLimit,
      selectedItemsCount,
    } = store
    const isMaxLimitReached = maxLimit !== null && selectedItemsCount >= maxLimit
    const [selectedIds, setSelectedIds] = useState<number[]>([])

    const handleScroll = (e: UIEvent) => {
      const target = e.target as HTMLDivElement
      const scrollHeight = target.scrollHeight
      const scrollTop = target.scrollTop
      const offsetHeight = target.offsetHeight

      if (loading) return
      if (!hasMore) return
      if (scrollHeight <= scrollTop + offsetHeight + 100) {
        loadMore()
      }
    }

    const handleSelect = (item: Inbox) => {
      onChange?.(item)
      store.selectTypeItem(item)
    }

    const isSelected = (id: number | undefined) => {
      if (!id) return false

      return store.isSelected(id)
    }

    useEffect(() => {
      setSelectedIds(store.selectedItemsListIds)

      if (!loading) {
        init()
      }
    }, [])

    return (
      <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={290} onScroll={handleScroll}>
        <div className={styles.list}>
          {[...selectedIds, ...itemsListIds].map((id, index) => {
            const item = getItemById(id)
            if (!item) return

            const isDisabled = isMaxLimitReached && !isSelected(id)
            const tooltipProps = isMaxLimitReached ? getLimitReachedTooltip() : undefined
            const inputCheckboxProps = options?.selectedMode
              ? {
                  checked: isSelected(id),
                  onChecked: () => handleSelect(item),
                }
              : undefined

            return (
              <Fragment key={item.id}>
                <InboxDropdownCard
                  item={item}
                  InputCheckboxProps={inputCheckboxProps}
                  onChange={handleSelect}
                  tooltipProps={tooltipProps}
                  disabled={isDisabled}
                />
                {index === selectedIds.length - 1 && <div className={styles.divider}></div>}
              </Fragment>
            )
          })}
          {!hasItems && !loading && (
            <div className={styles.epmtyList}>
              <Typography variant={'body-rg-regular'} ariaLabel={'NotFound'}>
                Not found
              </Typography>
            </div>
          )}
          {loading && (
            <div className={styles.spinner}>
              <SpinnerLoader ariaLabel={'InboxesDropdownContentItems'} />
            </div>
          )}
        </div>
      </Scrollbar>
    )
  }
)
